import { useQuery } from '@tanstack/react-query';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import { toaster } from 'components/v2/toast';
import { Button } from 'primereact/button';
import { Column, ColumnBodyOptions } from 'primereact/column';
import React, { useMemo, useState } from 'react';
import {
  CompanyMigrationEpdModel,
  CompanyMigrationEpdOutcome,
  CompanyMigrationIssueCategory,
  CompanyMigrationIssueCode,
  CompanyMigrationIssueModel,
  CompanyMigrationRunModel,
  downloadAdminCompanyMigrationIssues,
  getAdminCompanyMigrationEpdIssues,
  getAdminCompanyMigrationEpds,
} from 'services/api/adminCompanyMigrationApi';
import { QUERY_KEYS } from 'services/api/constants';
import styled, { DefaultTheme } from 'styled-components';
import { getApiErrorDetail } from 'util/apiErrorUtils';

export const OUTCOME_LABELS: Record<CompanyMigrationEpdOutcome, string> = {
  Ready: 'Ready',
  ReadyWithFollowUps: 'Ready, owner follow-up',
  Blocked: 'Blocked',
};

const CATEGORY_LABELS: Record<CompanyMigrationIssueCategory, string> = {
  Blocker: 'Blocker',
  FollowUp: 'Owner follow-up',
  MappingNote: 'Mapping note',
};

const CODE_LABELS: Record<CompanyMigrationIssueCode, string> = {
  MissingRegistrationNumber: 'No registration number',
  NoVersions: 'No versions',
  MissingVersionNumber: 'Version without a number',
  DuplicateVersionNumber: 'Two versions with one number',
  MissingTextData: 'Version without text data',
  UnresolvedDigitalVersion: 'PCR has no digital version',
  InvalidMegaJsonType: 'Translation produced invalid data',
  TranslationFailed: 'Translation failed',
  InsertFailed: 'Saving the copy failed',
  VerificationFailed: 'Copy failed the check',
  CopyStructureMismatch: 'Copy differs in structure',
  StatusMismatch: 'Status would change',
  DashboardCardMismatch: 'Dashboard card would change',
  ContentMismatch: 'Copied content differs',
  FileMismatch: 'Files differ',
  RequiredValueMissing: 'Required value missing',
  InvalidValue: 'Value not offered in Dynamic Forms',
  ValidationError: 'Validation error',
  SourceValueMissing: 'Missing in the Simple Wizard',
  LiveValueUsed: 'Current value used',
  AccountManagerUsed: 'Account manager used as contact',
  FirstVersionDateFallback: 'First version date derived',
  DefaultTitleUsed: 'Title set to "Unnamed"',
  ValueDropped: 'Value not copied',
  ValueRefiled: 'Files regrouped',
};

const FIXED_BY_LABELS: Record<string, string> = {
  EpdOwner: 'EPD owner',
  Verifier: 'Verifier',
  Administrator: 'Administrator',
  LicenseeAdministrator: 'Licensee administrator',
};

const CATEGORY_ORDER: CompanyMigrationIssueCategory[] = ['Blocker', 'FollowUp', 'MappingNote'];

/** The "#" column of a table: the row's position, from 1. */
export const rowNumberColumn = () => (
  <Column header="#" body={(_: unknown, options: ColumnBodyOptions) => options.rowIndex + 1} style={{ width: '3.5rem' }} />
);

/** "SentForVerification" -> "Sent for verification". */
export const humanize = (value?: string | null) =>
  value
    ? value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase())
        .replace(/ ([A-Z])/g, (m) => m.toLowerCase())
    : '';

type Filter = { kind: 'all' } | { kind: 'outcome'; outcome: CompanyMigrationEpdOutcome } | { kind: 'notes' };

/**
 * The results of one dry run or rehearsal: what can be migrated, what is blocked, what the owners complete later in
 * Dynamic Forms and what the mapping changed. Validation gaps are follow-ups, never failures. Click an EPD to see its
 * issues below it.
 */
const CompanyMigrationRunResults: React.FunctionComponent<{ companyId: string; run: CompanyMigrationRunModel }> = ({
  companyId,
  run,
}) => {
  const [filter, setFilter] = useState<Filter>({ kind: 'all' });
  const [selectedEpd, setSelectedEpd] = useState<CompanyMigrationEpdModel | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const epdsQuery = useQuery({
    queryKey: [QUERY_KEYS.ADMIN_COMPANY_MIGRATION_EPDS, companyId, run.id],
    queryFn: () => getAdminCompanyMigrationEpds(companyId, run.id),
    refetchOnWindowFocus: false,
  });

  const filteredEpds = useMemo(() => {
    const epds = epdsQuery.data ?? [];
    switch (filter.kind) {
      case 'outcome':
        return epds.filter((epd) => epd.outcome === filter.outcome);
      case 'notes':
        return epds.filter((epd) => epd.noteCount > 0);
      default:
        return epds;
    }
  }, [epdsQuery.data, filter]);

  const applyFilter = (next: Filter) => {
    setFilter(next);
    setSelectedEpd(null);
  };

  const exportIssues = async () => {
    setIsExporting(true);
    try {
      const blob = await downloadAdminCompanyMigrationIssues(companyId, run.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `migration-issues-${new Date(run.requestedAt).toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toaster({ severity: 'error', summary: 'Error', details: getApiErrorDetail(error) ?? 'Failed to export the issues.' });
    } finally {
      setIsExporting(false);
    }
  };

  const tile = (label: string, value: number, tone: Tone, target: Filter, hint?: string) => {
    const isActive = JSON.stringify(filter) === JSON.stringify(target);
    return (
      <Tile $tone={tone} $active={isActive} onClick={() => applyFilter(isActive ? { kind: 'all' } : target)} type="button">
        <TileValue>{value}</TileValue>
        <TileLabel>{label}</TileLabel>
        {hint && <TileHint>{hint}</TileHint>}
      </Tile>
    );
  };

  const filterLabel =
    filter.kind === 'outcome' ? OUTCOME_LABELS[filter.outcome] : filter.kind === 'notes' ? 'With mapping notes' : null;

  return (
    <Results>
      <ResultsHeader>
        <h3>
          Results: {humanize(run.operation)} of {new Date(run.requestedAt).toLocaleString()}
        </h3>
        <Button
          label="Export issues (CSV)"
          icon="pi pi-download"
          className="p-button-sm p-button-outlined"
          loading={isExporting}
          onClick={exportIssues}
        />
      </ResultsHeader>

      <Tiles>
        {tile('Migrated', run.migratedCount, 'green', { kind: 'all' }, `${run.readyCount} ready without follow-ups`)}
        {tile('Blocked', run.blockedCount, 'red', { kind: 'outcome', outcome: 'Blocked' }, 'Could not be migrated')}
        {tile(
          'Owner follow-up',
          run.followUpEpdCount,
          'yellow',
          { kind: 'outcome', outcome: 'ReadyWithFollowUps' },
          'Migrated; completed later in Dynamic Forms'
        )}
        {tile('Mapping notes', run.notedEpdCount, 'blue', { kind: 'notes' }, 'Migrated; review what the mapping changed')}
      </Tiles>

      <SectionTitle>
        EPDs
        {filterLabel && (
          <FilterChip>
            {filterLabel}
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-sm"
              onClick={() => applyFilter({ kind: 'all' })}
              aria-label="Clear the filter"
            />
          </FilterChip>
        )}
        <Hint>Click an EPD to see its issues.</Hint>
      </SectionTitle>
      {epdsQuery.isError && <ErrorText>{getApiErrorDetail(epdsQuery.error) ?? 'Failed to load the EPDs.'}</ErrorText>}
      <DataTableStyled
        value={filteredEpds}
        loading={epdsQuery.isLoading}
        emptyMessage="No EPDs."
        dataKey="epdHeadId"
        selectionMode="single"
        selection={selectedEpd}
        onSelectionChange={(e) => setSelectedEpd((e.value as CompanyMigrationEpdModel | null) ?? null)}
        rowClassName={() => 'migration-selectable-row'}
        paginator={filteredEpds.length > 25}
        rows={25}
      >
        {rowNumberColumn()}
        <Column field="registrationNumber" header="Reg. no." sortable style={{ width: '7rem' }} />
        <Column field="title" header="Title" sortable />
        <Column
          field="simpleWizardStatus"
          header="Simple Wizard status"
          sortable
          body={(row: CompanyMigrationEpdModel) => humanize(row.simpleWizardStatus)}
        />
        <Column
          field="outcome"
          header="Outcome"
          sortable
          body={(row: CompanyMigrationEpdModel) => <OutcomeBadge outcome={row.outcome} />}
        />
        <Column field="followUpCount" header="Follow-ups" sortable style={{ width: '7rem' }} />
        <Column field="noteCount" header="Notes" sortable style={{ width: '6rem' }} />
        <Column field="blockerMessage" header="Why blocked" />
      </DataTableStyled>

      {selectedEpd && <EpdIssues companyId={companyId} runId={run.id} epd={selectedEpd} />}
    </Results>
  );
};

/** Everything found for the selected EPD: blockers first, then owner follow-ups, then mapping notes. */
const EpdIssues: React.FunctionComponent<{
  companyId: string;
  runId: string;
  epd: CompanyMigrationEpdModel;
}> = ({ companyId, runId, epd }) => {
  const issuesQuery = useQuery({
    queryKey: [QUERY_KEYS.ADMIN_COMPANY_MIGRATION_EPD_ISSUES, companyId, runId, epd.epdHeadId],
    queryFn: () => getAdminCompanyMigrationEpdIssues(companyId, runId, epd.epdHeadId),
    refetchOnWindowFocus: false,
  });

  const issues = useMemo(
    () =>
      [...(issuesQuery.data ?? [])].sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)),
    [issuesQuery.data]
  );

  return (
    <IssuesPanel>
      <SectionTitle>
        Issues of {[epd.registrationNumber, epd.title].filter((part) => !!part).join(' ')}
        <OutcomeBadge outcome={epd.outcome} />
        <Hint>
          Follow-ups are not an obstacle to the migration: the owner completes them in Dynamic Forms. Mapping notes are for
          review only.
        </Hint>
      </SectionTitle>
      {issuesQuery.isError && <ErrorText>{getApiErrorDetail(issuesQuery.error) ?? 'Failed to load the issues.'}</ErrorText>}
      <DataTableStyled
        value={issues}
        loading={issuesQuery.isLoading}
        emptyMessage="Nothing to report: the EPD migrates as it is."
      >
        {rowNumberColumn()}
        <Column
          header="Kind"
          body={(row: CompanyMigrationIssueModel) => <CategoryBadge category={row.category} />}
          style={{ width: '10rem' }}
        />
        <Column
          header="Field"
          body={(row: CompanyMigrationIssueModel) => row.propertyLabel ?? CODE_LABELS[row.code] ?? humanize(row.code)}
        />
        <Column field="section" header="Step" />
        <Column
          header="Who completes it"
          body={(row: CompanyMigrationIssueModel) =>
            row.fixedBy ? FIXED_BY_LABELS[row.fixedBy] ?? humanize(row.fixedBy) : ''
          }
        />
        <Column field="versionNumber" header="Version" style={{ width: '6rem' }} />
        <Column field="message" header="Details" style={{ minWidth: '20rem' }} />
      </DataTableStyled>
    </IssuesPanel>
  );
};

export type Tone = 'green' | 'red' | 'yellow' | 'blue' | 'gray';

export const toneColors = (theme: DefaultTheme, tone: Tone) => {
  switch (tone) {
    case 'green':
      return { text: theme.colors.statusLabelGreenText, bg: theme.colors.statusLabelGreenBg };
    case 'red':
      return { text: theme.colors.statusLabelRedText, bg: theme.colors.statusLabelRedBg };
    case 'yellow':
      return { text: theme.colors.statusLabelYellowText, bg: theme.colors.statusLabelYellowBg };
    case 'blue':
      return { text: theme.colors.statusLabelBlueText, bg: theme.colors.statusLabelBlueBg };
    default:
      return { text: theme.colors.statusLabelGrayText, bg: theme.colors.statusLabelGrayBg };
  }
};

const OUTCOME_TONES: Record<CompanyMigrationEpdOutcome, Tone> = {
  Ready: 'green',
  ReadyWithFollowUps: 'yellow',
  Blocked: 'red',
};
const CATEGORY_TONES: Record<CompanyMigrationIssueCategory, Tone> = {
  Blocker: 'red',
  FollowUp: 'yellow',
  MappingNote: 'blue',
};

export const OutcomeBadge: React.FunctionComponent<{ outcome: CompanyMigrationEpdOutcome }> = ({ outcome }) => (
  <Badge $tone={OUTCOME_TONES[outcome]}>{OUTCOME_LABELS[outcome] ?? outcome}</Badge>
);

const CategoryBadge: React.FunctionComponent<{ category: CompanyMigrationIssueCategory }> = ({ category }) => (
  <Badge $tone={CATEGORY_TONES[category]}>{CATEGORY_LABELS[category] ?? category}</Badge>
);

export const Badge = styled.span<{ $tone: Tone }>`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) => toneColors(props.theme, props.$tone).text};
  background: ${(props) => toneColors(props.theme, props.$tone).bg};
`;

const Results = styled.div`
  margin-top: 1.5rem;

  .migration-selectable-row {
    cursor: pointer;
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 0.75rem;
  margin: 0.75rem 0 1.25rem;
`;

const Tile = styled.button<{ $tone: Tone; $active: boolean }>`
  text-align: left;
  border: 2px solid ${(props) => (props.$active ? toneColors(props.theme, props.$tone).text : 'transparent')};
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: ${(props) => toneColors(props.theme, props.$tone).text};
  background: ${(props) => toneColors(props.theme, props.$tone).bg};
`;

const TileValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
`;

const TileLabel = styled.div`
  font-weight: 600;
`;

const TileHint = styled.div`
  font-size: 0.8125rem;
  opacity: 0.85;
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1.25rem 0 0.5rem;
`;

const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  font-weight: 400;
  font-size: 0.875rem;
  padding-left: 0.5rem;
  border-radius: 1rem;
  background: ${(props) => props.theme.colors.statusLabelGrayBg};
`;

const Hint = styled.span`
  font-weight: 400;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.statusLabelGrayText};
`;

const IssuesPanel = styled.div`
  margin: 0.5rem 0 1.5rem;
  padding-left: 1rem;
  border-left: 3px solid ${(props) => props.theme.colors.statusLabelGrayBg};
`;

const ErrorText = styled.div`
  color: ${(props) => props.theme.colors.statusLabelRedText};
  padding: 0.5rem 0;
`;

export default CompanyMigrationRunResults;
